import { fetchWithAuth } from "@/lib/api";
import { API_BASE } from "@/config/api";

export interface FeedbackAttachmentPublic {
  attachment_id: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
}

export interface FeedbackPublic {
  feedback_id: string;
  user_id: string;
  user_email: string;
  category: "bug" | "feature" | "improvement" | "other";
  priority: "low" | "medium" | "high" | "critical";
  message: string;
  status: "new" | "reviewed" | "resolved";
  attachments?: FeedbackAttachmentPublic[];
  created_at: string;
  updated_at: string;
}

export interface CreateFeedbackPayload {
  category: string;
  message: string;
  attachments?: CreateFeedbackAttachmentPayload[];
}

export interface CreateFeedbackAttachmentPayload {
  attachment_id: string;
  object_key: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
}

export interface UploadIntentFileSpec {
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface UploadIntentResult {
  attachment_id: string;
  object_key: string;
  upload_url: string;
  form_fields: Record<string, string>;
  expires_at: string;
  max_size_bytes: number;
}

interface UploadIntentResponseShape {
  attachment_id?: string;
  object_key?: string;
  upload_url?: string;
  form_fields?: Record<string, string>;
  expires_at?: string;
  max_size_bytes?: number;
  AttachmentID?: string;
  ObjectKey?: string;
  UploadURL?: string;
  FormFields?: Record<string, string>;
  ExpiresAt?: string;
  MaxSizeBytes?: number;
}

function normalizeUploadIntent(intent: UploadIntentResponseShape): UploadIntentResult {
  return {
    attachment_id: intent.attachment_id ?? intent.AttachmentID ?? "",
    object_key: intent.object_key ?? intent.ObjectKey ?? "",
    upload_url: intent.upload_url ?? intent.UploadURL ?? "",
    form_fields: intent.form_fields ?? intent.FormFields ?? {},
    expires_at: intent.expires_at ?? intent.ExpiresAt ?? "",
    max_size_bytes: intent.max_size_bytes ?? intent.MaxSizeBytes ?? 0,
  };
}

export async function requestFeedbackUploadIntents(
  files: UploadIntentFileSpec[]
): Promise<UploadIntentResult[]> {
  const response = await fetchWithAuth(`${API_BASE}/feedbacks/attachments/intents`, {
    method: "POST",
    body: JSON.stringify({ files }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to get upload URLs");
  }

  const data = await response.json();
  const attachments = Array.isArray(data.attachments) ? data.attachments : [];
  return attachments.map((item: UploadIntentResponseShape) => normalizeUploadIntent(item));
}

export async function uploadFileToSignedUrl(
  uploadUrl: string,
  formFields: Record<string, string>,
  file: File
): Promise<void> {
  const formData = new FormData();
  Object.entries(formFields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  // GCS expects the file field in the multipart payload.
  formData.append("file", file);

  const response = await fetch(uploadUrl, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }
}

export async function createFeedback(payload: CreateFeedbackPayload): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/feedbacks`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to submit feedback");
  }
}

export async function listAdminFeedbacks(): Promise<FeedbackPublic[]> {
  const response = await fetchWithAuth(`${API_BASE}/feedbacks/admin`, {
    method: "GET",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch feedbacks");
  }

  const data = await response.json();
  return data.feedbacks ?? [];
}

export async function updateFeedbackStatus(
  feedbackId: string,
  status: "new" | "reviewed" | "resolved"
): Promise<void> {
  await updateAdminFeedback(feedbackId, { status });
}

export async function updateAdminFeedback(
  feedbackId: string,
  payload: {
    status?: "new" | "reviewed" | "resolved";
    priority?: "low" | "medium" | "high" | "critical";
    category?: "bug" | "feature" | "improvement" | "other";
  }
): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/feedbacks/admin/${feedbackId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error("Failed to update feedback");
  }
}

export async function deleteFeedback(feedbackId: string): Promise<void> {
  const response = await fetchWithAuth(`${API_BASE}/feedbacks/admin/${feedbackId}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error("Failed to delete feedback");
  }
}

export interface FeedbackAttachmentDownload {
  download_url: string;
  expires_at: string;
  filename: string;
  content_type: string;
}

export async function getFeedbackAttachmentDownloadUrl(
  feedbackId: string,
  attachmentId: string
): Promise<FeedbackAttachmentDownload> {
  const response = await fetchWithAuth(
    `${API_BASE}/feedbacks/admin/${feedbackId}/attachments/${attachmentId}/download`,
    { method: "GET" }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to get download URL");
  }

  return response.json();
}
